import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { useSelector } from 'react-redux';
import { getTrustedIssuerList, trustedIssuerListSelector } from '../../../state/slices/trustedissuerlist';
import { TrustedIssuerList, useAppDispatch } from '../../../state';
import { Column } from 'primereact/column';
import { ClipboardCopyElement, ReadMorePanel, TextToHtml } from '../../molecules';

export const RegisterOrganization: FC = () => {
    const {t} = useTranslation();
    const companyExampleJson = {
        "legalName": "Credenco B.V.",
        "url": "https://www.credenco.com",
        "telephone": "+31 85 820 97 25",
        "email": "info@credenco.com",
        "location": {
            "address": {
                "postalCode": "2411 PV",
                "streetAddress": "Tolnasingel 3",
                "addressLocality": "Bodegraven, The Netherlands",
            },
            "geo": {
                "latitude": "52.0682894",
                "longitude": "4.762281"
            }
        }
    };
    const exampleDidDocument = {
        "@context": [
            "https://www.w3.org/ns/did/v1",
            "https://w3id.org/security/suites/jws-2020/v1"
        ],
        "id": "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0",
        "verificationMethod": [
            {
                "id": "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc",
                "type": "JsonWebKey2020",
                "controller": "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0",
                "publicKeyJwk": {
                    "kty": "EC",
                    "crv": "P-256",
                    "kid": "pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc",
                    "x": "gaZgpgsGhk5wsKt13kOxqWdDP68DVuEkrc15JoH5dxc",
                    "y": "Q6Ff8h-T3TrkWBj6kx1PO1uh1uMx8nojcRBc2eCXqlI"
                }
            }
        ],
        "assertionMethod": [
            "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc"
        ],
        "authentication": [
            "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc"
        ],
        "capabilityInvocation": [
            "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc"
        ],
        "capabilityDelegation": [
            "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc"
        ],
        "keyAgreement": [
            "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#pg01hv1VwLsBZUR8nKajsWT0DyxszDpOl7jLT-NyyTc"
        ],
        "service": [
            {
                "type": "LinkedVerifiablePresentation",
                "serviceEndpoint": "https://wallet.acc.credenco.com/api/linkedvp/52e1f2df-9e77-48a9-a838-cba39d34fd44/2f21c957-768b-4af6-bb1e-8edc0cfa9552",
                "id": "2f21c957-768b-4af6-bb1e-8edc0cfa9552"
            },
            {
                "type": "LinkedVerifiablePresentation",
                "id": "96c1a67e-af06-4c6c-aa1b-048393214f95",
                "serviceEndpoint": "https://wallet.acc.credenco.com/api/linkedvp/52e1f2df-9e77-48a9-a838-cba39d34fd44/96c1a67e-af06-4c6c-aa1b-048393214f95"
            },
            {
                "type": "eInvoice",
                "serviceEndpoint": [
                    {
                        "transport_type": "HTTP",
                        "description": "Secure document exchange over HTTPS",
                        "uri": "https://wallet.acc.credenco.com/public/inbox/52e1f2df-9e77-48a9-a838-cba39d34fd44",
                        "supported_document_types": [
                            {
                                "type": "Invoice",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
                            },
                            {
                                "type": "CreditNote",
                                "specification": "EN 16931",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2"
                            }
                        ]
                    },
                    {
                        "transport_type": "PEPPOL",
                        "description": "Document exchange via PEPPOL 4-corner model",
                        "participant_id": "iso6523-actorid-upis::9944:nl807319508b01",
                        "supported_document_types": [
                            {
                                "type": "Invoice",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
                            },
                            {
                                "type": "CreditNote",
                                "specification": "EN 16931",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2"
                            },
                            {
                                "type": "Order",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:Order-2"
                            },
                            {
                                "type": "OrderResponse",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:OrderResponse-2"
                            },
                            {
                                "type": "DespatchAdvice",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:DespatchAdvice-2"
                            },
                            {
                                "type": "ApplicationResponse",
                                "specification": "UBL 2.1",
                                "urn": "urn:oasis:names:specification:ubl:schema:xsd:ApplicationResponse-2"
                            }
                        ],
                        "unsupported_document_types": [
                            {
                                "type": "Timesheet",
                                "description": "Timesheet submission is not supported in PEPPOL",
                                "specification": "Non-UBL",
                                "urn": "urn:example:timesheet-document"
                            },
                            {
                                "type": "CustomInvoice",
                                "description": "Non-standard invoice format not supported",
                                "specification": "Proprietary",
                                "urn": "urn:example:custom-invoice-format"
                            }
                        ]
                    }
                ],
                "id": "eInvoice"
            },
            {
                "type": "OID4VP",
                "id": "did:web:furnitrade.acc.credenco.com:did:deead35b-8ee3-41c4-9108-46e8620260e0#oid4vp",
                "serviceEndpoint": "https://furnitrade.acc.credenco.com"
            }
        ]
    };

    const supportedDids = [
        {method: 'Web', registration: 'Manually register the DID'},
        {method: 'EBSI', registration: 'Automatically registered by crawling the EBSI DID Registry'}
    ];
    const supportedServiceTypes = [
        {serviceType: 'LinkedVerifiablePresentation', description: 'Contains the verifiable business information as link to a verifiable presentation.'},
        {serviceType: 'eInvoice', description: 'Contains the information of the supported eInvoice endpoints.'},
        {serviceType: 'OID4VCI', description: 'Contains the OpenID4 Verifiable Credential Issuance endpoint.'},
        {serviceType: 'OID4VP', description: 'Contains the OpenID4 Verifiable Presentation endpoint.'}
    ];
    const supportedCredentialTypes = [
        {credentialType: 'LPID', description: 'Contains the legal person identifier issued by the Business Registry.', format: 'SD-JWT'},
        {credentialType: 'VAT', description: 'Contains the VAT ID issued by the Tax Authority.', format: 'SD-JWT'},
        {credentialType: 'ContactInfo', description: 'Contains the (self issued) contact information of the organization.', format: 'SD-JWT'},
        {credentialType: 'IBANLegalPerson', description: 'Contains the IBAN of the legal person issued by a bank.', format: 'SD-JWT'}
    ];
    return (
        <div>
            <div className="flex flex-column">
                <h1 className="font-bold text-xl mt-3 mb-1">{t('screens.registerOrganization.title')}</h1>
                <p>{t('screens.registerOrganization.subTitle')}</p>
            </div>
            <div className="flex flex-column">
                <h2 className="font-bold text-m mt-3 mb-1">{t('screens.registerOrganization.supportedDids.title')}</h2>
                <p>{t('screens.registerOrganization.supportedDids.description')}</p>
                <DataTable value={supportedDids} tableStyle={{minWidth: '50rem'}}>
                    <Column field="method" header="DID method"></Column>
                    <Column field="registration" header="Organization registration"></Column>
                </DataTable>
            </div>
            <div className="flex flex-column">
                <h2 className="font-bold text-m mt-3 mb-1">{t('screens.registerOrganization.supportedServiceTypes.title')}</h2>
                <p>{t('screens.registerOrganization.supportedServiceTypes.description')}</p>
                <DataTable value={supportedServiceTypes} tableStyle={{minWidth: '50rem'}}>
                    <Column field="serviceType" header="Service type"></Column>
                    <Column field="description" header="Description"></Column>
                </DataTable>
            </div>
            <div className="flex flex-column">
                <h2 className="font-bold text-m mt-3 mb-1">{t('screens.registerOrganization.credentialTypes.title')}</h2>
                <p>{t('screens.registerOrganization.credentialTypes.description')}</p>
                <DataTable value={supportedCredentialTypes} tableStyle={{minWidth: '50rem'}}>
                    <Column field="credentialType" header="Credential type"></Column>
                    <Column field="description" header="Description"></Column>
                    <Column field="format" header="Format"></Column>
                </DataTable>
                <p><ReadMorePanel bodyPart1={<TextToHtml value={t('screens.addDid.schemaInfo.short.body')}/>}
                                  bodyPart2={<div>
                                      <TextToHtml value={t('screens.addDid.schemaInfo.long.body')}/>
                                      <pre style={{
                                          padding: '16px',
                                          backgroundColor: 'rgb(247, 250, 252)',
                                          borderRadius: '8px',
                                          borderStyle: 'solid',
                                          borderWidth: '1px',
                                          borderColor: 'rgb(226, 232, 240)'
                                      }}>{JSON.stringify(companyExampleJson, null, 2)}
                            </pre>
                                  </div>}
                /></p>
            </div>
            <div className="flex flex-column">
                <h2 className="font-bold text-m mt-3 mb-1">{t('screens.registerOrganization.exampleDid.title')}</h2>
                <p><ReadMorePanel bodyPart1={
                    <TextToHtml value={t('screens.registerOrganization.exampleDid.short')}/>
                }
                                  bodyPart2={
                                      <div>
                                          <TextToHtml value={t('screens.registerOrganization.exampleDid.long')}/>
                                          <pre style={{
                                              padding: '16px',
                                              backgroundColor: 'rgb(247, 250, 252)',
                                              borderRadius: '8px',
                                              borderStyle: 'solid',
                                              borderWidth: '1px',
                                              borderColor: 'rgb(226, 232, 240)'
                                          }}>
                                       {JSON.stringify(exampleDidDocument, null, 2)}
                                   </pre>
                                      </div>
                                  }
                /></p>
            </div>
            </div>
            );
            };
